import { useState } from "react";
import { Btn } from "../UI";
import { Sparkles, BookOpen, Globe, ExternalLink, Loader2 } from "lucide-react";

const TABS = [["ai", "AI draft", Sparkles], ["clinic", "Clinic handouts", BookOpen], ["public", "Trusted public links", Globe]];

function AiTab({ busy, onCreate }) {
  return (
    <div className="space-y-3" data-testid="edu-source-ai">
      <p className="text-xs text-[#6D4C41]">Writes a plain-language draft from the diagnosis code and title only. No client name, dates, or chart details are sent. A clinician must review and approve the draft before the client can see it.</p>
      <Btn data-testid="edu-ai-generate" disabled={busy} onClick={() => onCreate({ source: "ai" })}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} {busy ? "Writing the draft, this can take up to 30 seconds" : "Write AI draft"}
      </Btn>
    </div>
  );
}

function ClinicTab({ rows, code, busy, onCreate }) {
  if (!rows.length) return <p className="text-xs text-[#7B6157]" data-testid="edu-clinic-empty">No clinic handout matches {code || "this diagnosis"} yet. Admins add them in Settings, Patient Education Library.</p>;
  return (
    <div className="space-y-2" data-testid="edu-source-clinic">
      {rows.map((x) => (
        <div key={x.id} className="rounded-xl border border-[#EFEBE0] bg-white p-3" data-testid={`edu-clinic-${x.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0"><div className="text-sm font-semibold text-[#3E2723]">{x.title}</div><div className="font-mono text-[10px] text-[#84675B]">{x.code}</div></div>
            <Btn variant="outline" data-testid={`edu-clinic-use-${x.id}`} disabled={busy} onClick={() => onCreate({ source: "clinic", handout_id: x.id })}>Use this</Btn>
          </div>
          <p className="mt-1 line-clamp-2 text-xs text-[#6D4C41]">{x.body}</p>
        </div>
      ))}
    </div>
  );
}

function PublicTab({ rows, busy, onCreate }) {
  const [picked, setPicked] = useState(new Set());
  const toggle = (url) => setPicked((s) => { const n = new Set(s); n.has(url) ? n.delete(url) : n.add(url); return n; });
  if (!rows.length) return <p className="text-xs text-[#7B6157]" data-testid="edu-public-empty">No trusted public links found for this diagnosis.</p>;
  return (
    <div className="space-y-2" data-testid="edu-source-public">
      {rows.map((x, i) => (
        <label key={x.url} data-testid={`edu-public-${i}`} className={`flex cursor-pointer items-start gap-2.5 rounded-xl border p-3 transition-colors ${picked.has(x.url) ? "border-[#4E342E] bg-[#F5EFE6]" : "border-[#EFEBE0] bg-white hover:bg-[#FDFCF8]"}`}>
          <input type="checkbox" checked={picked.has(x.url)} onChange={() => toggle(x.url)} className="mt-0.5 h-4 w-4 accent-[#4E342E]" data-testid={`edu-public-check-${i}`} />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-[#3E2723]">{x.label}</span>
            {x.summary && <span className="mt-0.5 block line-clamp-2 text-xs text-[#6D4C41]">{x.summary}</span>}
            <span className="mt-0.5 block text-[10px] text-[#84675B]">{x.source}</span>
          </span>
          <a href={x.url} target="_blank" rel="noreferrer" aria-label={`Open ${x.label}`} onClick={(e) => e.stopPropagation()} className="text-[#7B6157] hover:text-[#4E342E]"><ExternalLink size={13} /></a>
        </label>
      ))}
      <Btn data-testid="edu-public-create" disabled={busy || !picked.size}
        onClick={() => onCreate({ source: "public", links: rows.filter((x) => picked.has(x.url)).map((x) => ({ label: x.label, url: x.url })) })}>
        <Globe size={14} /> Create handout with {picked.size} link{picked.size === 1 ? "" : "s"}
      </Btn>
    </div>
  );
}

/** The three handout sources: AI draft, clinic library, and trusted public links. */
export function HandoutSources({ options, busy, onCreate }) {
  const [tab, setTab] = useState("ai");
  return (
    <div className="rounded-2xl border border-[#EFEBE0] bg-[#FDFCF8] p-4" data-testid="edu-sources">
      <div className="mb-3 text-sm font-bold text-[#3E2723]">Create a handout</div>
      <div className="mb-3 flex flex-wrap gap-1.5">
        {TABS.map(([k, label, Icon]) => (
          <button key={k} type="button" data-testid={`edu-tab-${k}`} onClick={() => setTab(k)}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${tab === k ? "bg-[#4E342E] text-white" : "bg-white text-[#6D4C41] ring-1 ring-[#E5DFD3] hover:bg-[#F5F2EB]"}`}>
            <Icon size={12} /> {label}{k === "clinic" ? ` (${options.clinic.length})` : k === "public" ? ` (${options.public.length})` : ""}
          </button>
        ))}
      </div>
      {tab === "ai" && <AiTab busy={busy} onCreate={onCreate} />}
      {tab === "clinic" && <ClinicTab rows={options.clinic} code={options.diagnosis.code} busy={busy} onCreate={onCreate} />}
      {tab === "public" && <PublicTab rows={options.public} busy={busy} onCreate={onCreate} />}
    </div>
  );
}
